import {ConditionGroup, ParamSchema} from "../segments/types";

export type Weekday = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";

export type RetryCombineMode = "union" | "first_match";

export type RetryRule = "day_1" | "skip_weekdays" | "peak_day" | "schedule";

export type PeakDay = number | "last";

export type CardPaymentsRetryPolicy = {
  max_day: number;
  combine: RetryCombineMode;
  order: RetryRule[];
  permitted_hours: number[];
  schedule: Record<string, number[]>;
  weekday_hours: Partial<Record<Weekday, number[]>>;
  skip_weekdays: Weekday[];
  peak_day: PeakDay[];
  peak_day_hours: number[];
  peak_min_day: number;
};

export type PaymentMethod = "card" | "ach" | "apple_pay";

export type PaymentMethodDisplayPolicy = {
  methods: PaymentMethod[];
  default: PaymentMethod;
};

export type PaymentFlowPolicies = {
  card_payments_retry: CardPaymentsRetryPolicy;
  payment_method_display: PaymentMethodDisplayPolicy;
};

export type PaymentFlowType = keyof PaymentFlowPolicies;
export const CARD_PAYMENTS_RETRY_FLOW_TYPE = "card_payments_retry" satisfies PaymentFlowType;
export const PAYMENT_METHOD_DISPLAY_FLOW_TYPE = "payment_method_display" satisfies PaymentFlowType;

export type PaymentFlowVariant<T extends PaymentFlowType = PaymentFlowType> = {
  weight: number;
  label: string;
  policy: PaymentFlowPolicies[T];
};

export type PaymentFlowConfig<T extends PaymentFlowType = PaymentFlowType> = {
  feature_flag: string;
  variants: Record<string, PaymentFlowVariant<T>>;
};

export type PaymentFlow<T extends PaymentFlowType = PaymentFlowType> = {
  id: number;
  public_id: string;
  name: string;
  priority: number;
  type: T;
  conditions: ConditionGroup;
  config: PaymentFlowConfig<T>;
  created_at: string;
  updated_at: string;
};

export type CardPaymentsRetryFlow = PaymentFlow<typeof CARD_PAYMENTS_RETRY_FLOW_TYPE>;
export type PaymentMethodDisplayFlow = PaymentFlow<typeof PAYMENT_METHOD_DISPLAY_FLOW_TYPE>;

export type PaymentFlowCreatePayload<T extends PaymentFlowType = PaymentFlowType> = Pick<
  PaymentFlow<T>,
  "name" | "priority" | "type" | "conditions" | "config"
>;

export type PaymentFlowUpdatePayload<T extends PaymentFlowType = PaymentFlowType> = Omit<
  PaymentFlowCreatePayload<T>,
  "priority"
>;

export type PaymentFlowFeatureFlag = {
  name: string;
  variant_weights: Record<string, number>;
};

export type PaymentFlowSchema = {
  parameters: ParamSchema[];
};
